import { err, ok } from "neverthrow";
import { auditDataSchemaRegistry } from "./registry.js";
const asRecord = (value) => typeof value === "object" && value !== null
    ? value
    : {};
/**
 * Narrow an audit entry's `data` to the typed shape for `type`, validating at
 * runtime. **Never throws.** A precise hit returns `ok(typedData)`; anything else
 * returns `err({ reason, raw })`, keeping the raw payload so callers can still
 * render legacy/unverified entries defensively.
 *
 * Precision is this function's return type, computed on demand — it cannot go
 * stale or disagree with a (possibly redacted) payload.
 */
export function tryGetAuditData(entry, type) {
    const raw = asRecord(entry.data);
    if (entry.type !== type) {
        return err({ reason: "unknown-type", raw });
    }
    const schema = auditDataSchemaRegistry[type];
    if (!schema) {
        return err({ reason: "unmigrated", raw });
    }
    const parsed = schema.safeParse(entry.data);
    if (!parsed.success) {
        return err({
            reason: "validation-failed",
            raw,
            issues: parsed.error.issues.map((issue) => issue.message),
        });
    }
    // Sound: `data` was parsed with the schema registered for `type`, so the
    // validated output is AuditDataFor<T>. This is the only cast, and it is
    // library-internal — consumers never see it.
    return ok(parsed.data);
}
/**
 * Typed data on a precise hit, else the raw payload (never null). Use when the
 * caller renders defensively from raw on a miss; use {@link tryGetAuditData} when the
 * miss reason matters.
 */
export function getAuditDataOrRaw(entry, type) {
    return tryGetAuditData(entry, type).match((data) => data, (imprecise) => imprecise.raw);
}
//# sourceMappingURL=accessor.js.map